import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { servicesAPI } from '../services/api';

const Services = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const servicesData = await servicesAPI.getAll();
        setServices(servicesData);
      } catch (err) {
        console.error('Error fetching services:', err);
        setError('Ошибка загрузки услуг');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const scrollToContact = () => {
    navigate({ pathname: '/', hash: '#contact' });
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--white)'
      }}>
        <p style={{ color: 'var(--charcoal)', fontSize: '1.125rem' }}>Загрузка услуг...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--white)'
      }}>
        <p style={{ color: 'var(--charcoal)', fontSize: '1.125rem' }}>{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section style={{
        background: `linear-gradient(135deg, rgba(212, 196, 176, 0.15) 0%, rgba(250, 249, 247, 1) 100%)`,
        padding: 'var(--space-xxxl) var(--space-l)',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 style={{ marginBottom: 'var(--space-l)' }}>Услуги</h1>
          <p style={{
            fontSize: '1.125rem',
            color: 'var(--warm-gray)',
            maxWidth: '700px',
            margin: '0 auto var(--space-xl)',
            lineHeight: '1.7'
          }}>
            Очные консультации в Санкт-Петербурге и дистанционное сопровождение. Выберите формат, который подходит вам.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'var(--space-xxl)',
            maxWidth: '1100px',
            margin: '0 auto'
          }}
          className="services-grid">
            {services.map((service, index) => (
              <Link
                key={service.id}
                to={`/services/${service.slug}`}
                style={{ textDecoration: 'none' }}
              >
                <div className="card" style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  padding: 0,
                  transition: 'transform 400ms ease, box-shadow 400ms ease'
                }}
                className="service-card">
                  <div style={{
                    height: '280px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <img
                      src={service.image_url}
                      alt={service.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 500ms ease'
                      }}
                      className="service-card-image"
                    />
                    <div style={{
                      position: 'absolute',
                      top: 'var(--space-l)',
                      right: 'var(--space-l)',
                      backgroundColor: 'var(--cream-white)',
                      padding: 'var(--space-s) var(--space-m)',
                      borderRadius: 'var(--radius-sm)',
                      fontFamily: 'var(--font-primary)',
                      fontSize: '1.125rem',
                      color: 'var(--warm-sage)',
                      fontWeight: 500
                    }}>
                      {service.price}
                    </div>
                  </div>
                  <div style={{
                    padding: 'var(--space-xl)',
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <h3 style={{
                      marginBottom: 'var(--space-m)',
                      color: 'var(--charcoal)',
                      fontSize: '1.5rem'
                    }}>
                      {service.title}
                    </h3>
                    <p style={{
                      color: 'var(--warm-gray)',
                      marginBottom: 'var(--space-l)',
                      fontSize: '1.0625rem',
                      lineHeight: '1.6',
                      flexGrow: 1
                    }}>
                      {service.description}
                    </p>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: 'var(--space-l)',
                      borderTop: '1px solid var(--soft-gray)'
                    }}>
                      <div>
                        <p className="caption" style={{
                          color: 'var(--warm-gray)',
                          marginBottom: 'var(--space-xs)'
                        }}>
                          Длительность
                        </p>
                        <p style={{
                          color: 'var(--charcoal)',
                          fontWeight: 500,
                          fontSize: '0.9375rem'
                        }}>
                          {service.duration}
                        </p>
                      </div>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--warm-sage)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 300ms ease'
                      }}
                      className="service-card-arrow">
                        <ArrowRight size={20} style={{ color: 'var(--cream-white)' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-bg-gray">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ marginBottom: 'var(--space-l)' }}>
            Не знаете, какая услуга подойдет?
          </h2>
          <p style={{
            fontSize: '1.0625rem',
            color: 'var(--warm-gray)',
            maxWidth: '600px',
            margin: '0 auto var(--space-xl)',
            lineHeight: '1.6'
          }}>
            Оставьте заявку, и мы обсудим вашу задачу. Подберем формат работы, который принесет максимальный результат.
          </p>
          <button className="btn-primary" onClick={scrollToContact}>
            Оставить заявку
          </button>
        </div>
      </section>

      <style>{`
        .service-card:hover {
          transform: translateY(-8px);
        }
        .service-card:hover .service-card-image {
          transform: scale(1.05);
        }
        .service-card:hover .service-card-arrow {
          transform: scale(1.1);
        }
        
        @media (max-width: 968px) {
          .services-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Services;
